import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography, debounce } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import Paper, { PaperProps } from '../components/Paper';
import { useSearchParams } from "react-router-dom";
import { GetSearchData } from '../services/SearchService';
import SearchIcon from '@mui/icons-material/Search';
import MultipleSelectCheckmarks from '../components/MultipleSelectCheckmarks';
import InfiniteScroll from 'react-infinite-scroll-component';
import responseSetPaperProps from '../utils/setPaperPropsUtils';
import theme from '../theme';
import TripleDotLoadingProgress from '../components/tools/TripleDotLoadingProgress';
import { GetFeatureToggles } from '../services/SettingsService';
import { FeatureToggle, extractFeatureToggles } from './settings/FeatureToggleSettingsPage';
import { GetAllUserCollections } from '../services/DigestService';

export const POSSIBLE_SEARCH_INS = ['title', 'authors', 'abstract'];
const POSSIBLE_SHOWS = ['all', 'read papers', 'bookmarked papers', 'arxiv papers'];
const POSSIBLE_ORDERINGS = ['personal relevance', 'query match', 'publication date'] //, 'number of likes on Scholar Inbox'];

const TYPING_DELAY = 300;
// TODO: increase if user scrolls further?
const NUMBER_OF_SEARCH_RESULTS = 200;

export default function SearchPage() {
  const [digestFeed, setDigestFeed] = useState<PaperProps[]>([]);
  const [titleHighlightIndexes, setTitleHighlightIndexes] = useState<number[][]>([]);
  const [authorsHighlightIndexes, setAuthorsHighlightIndexes] = useState<number[][]>([]);
  const [abstractHighlightIndexes, setAbstractHighlightIndexes] = useState<number[][]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchPrompt, setSearchPrompt] = useState<string>(searchParams.get('searchPrompt'));
  const [orderBy, setOrderBy] = useState<string>(POSSIBLE_ORDERINGS[1]);
  const [searchIn, setSearchIn] = useState<string[]>(['title']);
  const [show, setShow] = useState<string[]>(['all']);
  const [didSearch, setDidSearch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [paging, setPaging] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [featureToggles, setFeatureToggles] = useState<FeatureToggle[]>([]);
  const [allUserCollections, setAllUserCollections] = useState<string[]>([]);

  const inputCssClass = 'small';

  const getSearchData = () => {
    // if there is no search prompt, don't even ask the backend
    if (!searchPrompt) {
      setLoading(false);
      setHasNextPage(false);
      return;
    }

    const showString = show.join(',');
    const searchInString = searchIn.join(',');

    // paging = 0 as this method only runs on the initial load of data
    GetSearchData(searchPrompt, NUMBER_OF_SEARCH_RESULTS, searchInString, showString, orderBy, 0).then((response) => {
      let digest = responseSetPaperProps(response, searchIn);

      if (!didSearch) setDidSearch(true);
      setDigestFeed(digest);
      setTitleHighlightIndexes(response.title_highlighting_starts_ends && searchIn.some(e => e === 'title') ? response.title_highlighting_starts_ends : new Array(response.title_highlighting_starts_ends.length).fill([]));
      setAuthorsHighlightIndexes(response.authors_highlighting_starts_ends && searchIn.some(e => e === 'authors') ? response.authors_highlighting_starts_ends : new Array(response.authors_highlighting_starts_ends.length).fill([]));
      setAbstractHighlightIndexes(response.abstract_highlighting_starts_ends && searchIn.some(e => e === 'abstract') ? response.abstract_highlighting_starts_ends : new Array(response.abstract_highlighting_starts_ends.length).fill([]));
      setLoading(false);
      setHasNextPage(response.has_next);
      // setting paging to 1 rather than paging + 1 since we want to reset paging here, as getSearchData is only called whenever the search query/options are changed
      setPaging(1);
    });
  };

  const handleSearchSubmit = () => {
    event.preventDefault();
    getSearchData();
  };

  useEffect(() => {
    getSearchData();
  }, [searchPrompt, orderBy, searchIn, show]);

  const handleSearchInputs = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchPrompt(event.target.value);
  };

  const handleOrderingChange = (event: SelectChangeEvent) => {
    setLoading(true); setDigestFeed([]);
    setOrderBy(event.target.value as string);
  };

  useEffect(() => {
    const featureTogglesData = GetFeatureToggles();
    featureTogglesData.then(response => {
      if (response) {
        const data: FeatureToggle[] = extractFeatureToggles(response);
        setFeatureToggles(data);
      }
    });
    const allUserCollectionsRow = GetAllUserCollections();
    allUserCollectionsRow.then(response => {
      setAllUserCollections(response ? response : []);
    });
  }, []);

  const fetchNextPage = () => {
    console.log('fetching next');

    const searchInString = searchIn.join(',');
    const showString = show.join(',');

    const trendingData = GetSearchData(searchPrompt, NUMBER_OF_SEARCH_RESULTS, searchInString, showString, orderBy, paging);
    trendingData.then(response => {
      let digest = responseSetPaperProps(response, searchIn);
      setDigestFeed(digestFeed.concat(digest));
      setHasNextPage(response.has_next);
      setPaging(paging + 1);
    });

    console.log('done next');
  }

  const debouncedChangeHandler = useMemo(
    () => debounce(handleSearchInputs, TYPING_DELAY)
    , []);

  return (
    <Box sx={{ my: 4, maxWidth: theme.maxWidthContainer, mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', margin: '10px 0 20px 0', width: '100%' }}>
        <form onSubmit={handleSearchSubmit} style={{ width: '100%' }}>
          <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row', width: '100%', }} >
            <IconButton disabled={searchIn.length === 0} onClick={handleSearchSubmit} sx={{ width: '50px', height: '50px', margin: '5px 0 5px 0' }} >
              <SearchIcon sx={{
                transform: { sm: 'scale(1.8)' },
              }} />
            </IconButton>
            <TextField
              autoFocus
              disabled={searchIn.length === 0}
              onChange={debouncedChangeHandler}
              onInput={() => { setLoading(true); setDigestFeed([]); }}
              size={inputCssClass}
              sx={{
                fontSize: '50px',
                width: '100%',
                height: '100%',
                margin: '0 11px 0 0',
              }}
              color="secondary"
              variant='outlined'
              placeholder='Search ...'
              defaultValue={searchPrompt}
            />
          </Box>
        </form>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: {
          xs: 'center',
          md: 'left',
        },
        width: '100%',
      }}>

        <MultipleSelectCheckmarks
          selectedValues={searchIn}
          setSelectedValues={setSearchIn}
          options={POSSIBLE_SEARCH_INS}
          label='Search in'
          size={inputCssClass}
          sx={{
            width: {
              md: 250,
            },
            marginRight: 1
          }} />

        <MultipleSelectCheckmarks
          selectedValues={show}
          setSelectedValues={setShow}
          options={POSSIBLE_SHOWS}
          label='Show'
          size={inputCssClass}
          sx={{
            width: {
              md: 250,
            },
            marginRight: 1
          }} />

        <FormControl
          margin={'dense'}
          size={'small'}
          sx={{
            width: {
              md: 250,
            },
          }}>
          <InputLabel id="demo-simple-select-label" color='secondary'>Order by</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={orderBy}
            label="Order by"
            color='secondary'
            onChange={handleOrderingChange}
            size={inputCssClass}
          >
            {POSSIBLE_ORDERINGS.map((ordering) => (
              <MenuItem value={ordering}>{ordering}</MenuItem>
            ))}
          </Select>
        </FormControl>

      </Box>

      <Box>
        {loading && (
          <Box sx={{ justifyContent: 'center', display: 'flex', height: '40px', mt: 10 }}>
            <TripleDotLoadingProgress />
          </Box>
        )}
        {!loading && didSearch && digestFeed.length < 1 && (searchPrompt.length > 0) && (
          <Typography variant='h5' sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            No papers found satisfying your query
          </Typography>
        )}
        <InfiniteScroll
          dataLength={digestFeed.length}
          next={fetchNextPage}
          hasMore={hasNextPage}
          style={{ overflow: 'visible' }}
          loader={
            <Box sx={{ justifyContent: 'center', display: 'flex', height: '40px', mt: 5 }}>
              <TripleDotLoadingProgress />
            </Box>
          }
        >
          {digestFeed.map((paper, idx) => (
            <Paper {...paper}
              titleHighlightIndexes={titleHighlightIndexes[idx]}
              authorsHighlightIndexes={authorsHighlightIndexes[idx]}
              abstractHighlightIndexes={abstractHighlightIndexes[idx]}
              key={paper.title + paper.paperId}
              featureToggles={featureToggles}
              all_user_collections={allUserCollections}
            />
          ))}
        </InfiniteScroll>
      </Box>

    </Box>
  )
}